import React from 'react';
import { Link } from 'react-router-dom';
import Seo from './Seo';
import SiteNav from './SiteNav';
import SiteFooter from './SiteFooter';
import PageAtmosphere from './PageAtmosphere';
import './Marketing.css';

function IconJournal() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <rect x="10" y="8" width="28" height="32" rx="4" stroke="currentColor" strokeWidth="2" />
      <path d="M18 16h12M18 22h12M18 28h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M14 8v32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.45" />
    </svg>
  );
}

function IconMood() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <circle cx="24" cy="24" r="14" stroke="currentColor" strokeWidth="2" />
      <path
        d="M16 26c2.2 3.5 6 5.5 8 5.5s5.8-2 8-5.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="18.5" cy="20" r="1.75" fill="currentColor" />
      <circle cx="29.5" cy="20" r="1.75" fill="currentColor" />
    </svg>
  );
}

function IconGoals() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <circle cx="24" cy="24" r="14" stroke="currentColor" strokeWidth="2" />
      <circle cx="24" cy="24" r="8" stroke="currentColor" strokeWidth="2" opacity="0.55" />
      <circle cx="24" cy="24" r="2.5" fill="currentColor" />
      <path d="M24 10v4M24 34v4M10 24h4M34 24h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
    </svg>
  );
}

function IconCoach() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path
        d="M12 16a8 8 0 0 1 8-8h8a8 8 0 0 1 8 8v8a8 8 0 0 1-8 8h-4l-6 6v-6h-2a8 8 0 0 1-8-8v-8z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="20" cy="20" r="1.75" fill="currentColor" />
      <circle cx="24" cy="20" r="1.75" fill="currentColor" />
      <circle cx="28" cy="20" r="1.75" fill="currentColor" />
    </svg>
  );
}

const journey = [
  {
    num: '01',
    title: 'Journal',
    label: "Write what's real",
    text: 'Two minutes. Private. Honesty beats polish.',
    Icon: IconJournal,
  },
  {
    num: '02',
    title: 'Moods',
    label: 'See the pattern',
    text: 'Log how you feel. Spikes and steadiness become clear.',
    Icon: IconMood,
  },
  {
    num: '03',
    title: 'Goals',
    label: 'Aim at what matters',
    text: 'Targets that fit your real energy — not a fantasy week.',
    Icon: IconGoals,
  },
  {
    num: '04',
    title: 'AI coach',
    label: 'Think it through',
    text: 'Calm questions. You find the next step. No lectures.',
    Icon: IconCoach,
  },
];

const outcomes = [
  { title: 'Overcome anxiety', text: 'Name loops early. Choose a proportional next step.', Icon: IconMood },
  { title: 'Reach goals', text: 'Aim at what you can finish on busy days.', Icon: IconGoals },
  { title: 'Stay aware', text: 'Mood data turns vague weeks into clear patterns.', Icon: IconJournal },
  { title: 'Think with AI', text: 'A coach that asks — and leaves decisions to you.', Icon: IconCoach },
];

export default function ProductPage() {
  return (
    <div className="marketing-page marketing-page--atmosphere">
      <Seo
        title="How Life Journal works — 3AM AI"
        description="See how 3AM AI Life Journal works: journal → mood track → goals → AI coach. A clear path to overcome anxiety, reach goals, and think clearly."
        path="/product"
      />
      <PageAtmosphere />
      <SiteNav />
      <main>
        <header className="page-hero product-hero" id="journey">
          <div className="page-hero__inner m-animate-in">
            <p className="m-section__label">How it works</p>
            <h1>One quiet loop</h1>
            <p>
              Journal → moods → goals → AI coach. Built for people who want progress — not noise.
            </p>
            <ol className="product-flow" aria-label="Product flow">
              <li>Journal</li>
              <li>Moods</li>
              <li>Goals</li>
              <li>AI coach</li>
            </ol>
          </div>
        </header>

        <section className="product-journey" aria-label="Product journey">
          <div className="product-journey__rail" aria-hidden="true" />
          <div className="product-journey__grid">
            {journey.map((step, i) => {
              const { Icon } = step;
              return (
                <article
                  key={step.num}
                  className={`product-step m-animate-in m-animate-in--delay-${Math.min(i, 3)}`}
                >
                  <div className="product-step__icon">
                    <Icon />
                  </div>
                  <span className="product-step__num">{step.num}</span>
                  <h2>{step.title}</h2>
                  <p className="product-step__label">{step.label}</p>
                  <p className="product-step__text">{step.text}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="m-section product-why" id="why" aria-labelledby="why-heading">
          <p className="m-section__label">Why the pieces belong together</p>
          <h2 id="why-heading" className="m-section__title">
            Clarity compounds
          </h2>
          <p className="m-section__lede">
            Alone, each tool helps. Together — with a coach that remembers your thread — you stop
            restarting every Monday.
          </p>
          <div className="product-why__grid">
            {outcomes.map((item) => {
              const { Icon } = item;
              return (
                <article key={item.title} className="product-why__card">
                  <div className="product-why__icon">
                    <Icon />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              );
            })}
          </div>
          <div className="m-btn-row">
            <Link to="/signup" className="m-btn m-btn--primary">
              Start free — it takes a minute
            </Link>
            <Link to="/pricing" className="m-btn m-btn--ghost">
              See pricing
            </Link>
          </div>
        </section>

        <section className="cta-band" id="product-cta" aria-labelledby="product-cta-heading">
          <h2 id="product-cta-heading">Ready to try the loop?</h2>
          <p>Create your journal free. Bring one worry or one goal. Leave with a clearer next step.</p>
          <div className="m-btn-row">
            <Link to="/signup" className="m-btn m-btn--primary">
              Create your free account
            </Link>
            <Link to="/blog" className="m-btn m-btn--ghost">
              Read the blog
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
